import React, { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import AppModal from '../../../components/AppModal'
import AppButton from '../../../shared/AppButton'
import CustomFilterInput from '../../../shared/CustomFilterInput'
import claimsGQL from '../../../gql/claims'

const parseBundleIds = value =>
  value
    .replace(/[[\]]/g, '')
    .split(/[\s,]+/)
    .map(item => item.replace(/^["']|["']$/g, '').trim())
    .filter(Boolean)

const actionConfig = {
  cancel: {
    title: 'Bulk Cancel',
    successMessage: 'Successfully Cancelled!',
    errorMessage: 'Bulk cancellation failed.'
  },
  ready: {
    title: 'Bulk Ready',
    successMessage: 'Successfully Updated!',
    errorMessage: 'Bulk ready update failed.'
  }
}

const readyStatusOptions = [
  { id: 'error', name: 'Error' },
  { id: 'Abnormal Request', name: 'Abnormal Request' },
  { id: 'Exception Error', name: 'Exception Error' },
  { id: 'Validation Error', name: 'Validation Error' },
   { id: 'complete', name: 'Complete' }
]

function BulkCancelBundleModal ({
  open,
  onClose = () => {},
  onCompleted = () => {},
  action = 'cancel',
  isInline = false
}) {
  const { t } = useTranslation()
  const isReadyAction = action === 'ready'
  const [readyMode, setReadyMode] = useState('bundle')
  const [bundleIdsText, setBundleIdsText] = useState('')
  const [dateRangeInput, setDateRangeInput] = useState({
    from_date: '',
    to_date: '',
    status: 'Validation Error',
    payer_ids: []
  })
  const [payerToAdd, setPayerToAdd] = useState('')
  const [errors, setErrors] = useState([])
  const [touched, setTouched] = useState(false)
  const config = actionConfig[action] || actionConfig.cancel
  const payerOptions = useMemo(() => {
    try {
      const payersData = JSON.parse(localStorage.getItem('payersData')) || []
      return payersData
    } catch (e) {
      return []
    }
  }, [])
  const flatPayerOptions = useMemo(
    () =>
      payerOptions.flatMap(category =>
        (category?.payers || []).map(payer => ({
          id: payer.id,
          name: payer.name_en || payer.name || payer.id,
          licenseId: payer.license_id
        }))
      ),
    [payerOptions]
  )
  const selectedPayerOptions = useMemo(
    () =>
      flatPayerOptions.filter(payer =>
        dateRangeInput.payer_ids.includes(payer.id)
      ),
    [dateRangeInput.payer_ids, flatPayerOptions]
  )

  const requestBundleIds = useMemo(
    () => parseBundleIds(bundleIdsText),
    [bundleIdsText]
  )

  const handleCompleted = response => {
    const nphiesErrors = Array.isArray(response?.nphies_err)
      ? response.nphies_err
      : response?.nphies_err
        ? [response.nphies_err]
        : []

    if (nphiesErrors.length) {
      setErrors(nphiesErrors)
      toast.error(response?.message || t(config.errorMessage))
      return
    }

    toast.success(response?.message || t(config.successMessage))
    setBundleIdsText('')
    setReadyMode('bundle')
    setDateRangeInput({
      from_date: '',
      to_date: '',
      status: 'Validation Error',
      payer_ids: []
    })
    setPayerToAdd('')
    setErrors([])
    setTouched(false)
    onCompleted?.()
    onClose()
  }

  const handleError = error => {
    toast.error(error?.graphQLErrors?.[0]?.message || error.message)
  }

  const [bulkCancelBundleRequest, { loading: cancelLoading }] = useMutation(
    claimsGQL.BULK_CANCEL_BUNDLE_REQUEST,
    {
      onCompleted: data => handleCompleted(data?.bulkCancelBundleRequest),
      onError: handleError
    }
  )

  const [updateCancelledClaimsByBundleIds, { loading: readyLoading }] =
    useMutation(claimsGQL.UPDATE_CANCELLED_CLAIMS_BY_BUNDLE_IDS, {
      onCompleted: data =>
        handleCompleted(data?.updateCancelledClaimsByBundleIds),
      onError: handleError
    })

  const loading = action === 'ready' ? readyLoading : cancelLoading

  const handleClose = () => {
    if (loading) return
    setBundleIdsText('')
    setReadyMode('bundle')
    setDateRangeInput({
      from_date: '',
      to_date: '',
      status: 'Validation Error',
      payer_ids: []
    })
    setPayerToAdd('')
    setErrors([])
    setTouched(false)
    onClose()
  }

  const handleSubmit = () => {
    setTouched(true)
    setErrors([])

    if (isReadyAction) {
      if (readyMode === 'bundle') {
        if (!requestBundleIds.length) return
        updateCancelledClaimsByBundleIds({
          variables: {
            input: {
              request_bundle_ids: requestBundleIds
            }
          }
        })
        return
      }

      if (
        !dateRangeInput.from_date ||
        !dateRangeInput.to_date ||
        !dateRangeInput.status
      ) {
        return
      }

      updateCancelledClaimsByBundleIds({
        variables: {
          input: {
            from_date: dateRangeInput.from_date,
            to_date: dateRangeInput.to_date,
            status: dateRangeInput.status,
            ...(dateRangeInput.payer_ids.length
              ? { payer_ids: dateRangeInput.payer_ids }
              : {})
          }
        }
      })
      return
    }

    if (!requestBundleIds.length) return

    bulkCancelBundleRequest({
      variables: {
        input: {
          request_bundle_ids: requestBundleIds,
          request_name: 'claims'
        }
      }
    })
  }

  const bundleValidationError =
    touched &&
    (!isReadyAction || readyMode === 'bundle') &&
    !requestBundleIds.length
      ? t('At least one Request Bundle ID is required')
      : ''
  const dateRangeValidationError =
    touched &&
    isReadyAction &&
    readyMode === 'date' &&
    (!dateRangeInput.from_date ||
      !dateRangeInput.to_date ||
      !dateRangeInput.status)
      ? t('From Date, To Date, and Status are required')
      : ''

  const renderModeToggle = () =>
    isReadyAction ? (
      <div className='w-[300px] inline-flex items-center gap-1 p-0.5 rounded-md bg-gray-100 border border-gray-200 shrink-0'>
        {[
          { value: 'bundle', label: t('By Bundle IDs') },
          { value: 'date', label: t('By Date Range + Status') }
        ].map(mode => (
          <button
            key={mode.value}
            type='button'
            className={`flex-1 basis-0 rounded px-2.5 py-1 text-center text-[11px] font-bold transition-all ${
              readyMode === mode.value
                ? 'bg-[#0070C2] text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/60'
            }`}
            onClick={() => {
              setReadyMode(mode.value)
              setTouched(false)
              setErrors([])
            }}
            disabled={loading}
          >
            {mode.label}
          </button>
        ))}
      </div>
    ) : null

  const formBody = (
    <div className='space-y-3'>
      {!isInline && renderModeToggle()}

      {(!isReadyAction || readyMode === 'bundle') && (
        <CustomFilterInput
          label={t('Request Bundle IDs')}
          name='request_bundle_ids'
          type='textarea'
          rows={6}
          placeholder={t(
            'Paste Request Bundle IDs, separated by line or comma'
          )}
          value={bundleIdsText}
          onChange={event => setBundleIdsText(event.target.value)}
          onBlur={() => setTouched(true)}
          error={bundleValidationError}
          touched={touched}
        />
      )}

      {isReadyAction && readyMode === 'date' && (
        <div className='space-y-3'>
          <div className='grid grid-cols-1 gap-2 md:grid-cols-3'>
            <CustomFilterInput
              label={t('From Date')}
              name='from_date'
              type='date'
              value={dateRangeInput.from_date}
              onChange={event =>
                setDateRangeInput(prev => ({
                  ...prev,
                  from_date: event.target.value
                }))
              }
              onBlur={() => setTouched(true)}
            />
            <CustomFilterInput
              label={t('To Date')}
              name='to_date'
              type='date'
              value={dateRangeInput.to_date}
              onChange={event =>
                setDateRangeInput(prev => ({
                  ...prev,
                  to_date: event.target.value
                }))
              }
              onBlur={() => setTouched(true)}
            />
             <CustomFilterInput
            label={t('Claim Outcome')}
            name='status'
            isSelect
            value={dateRangeInput.status}
            placeholder={t('Select Claim Outcome')}
            options={readyStatusOptions.map(option => ({
              ...option,
              name: t(option.name)
            }))}
            onChange={event =>
              setDateRangeInput(prev => ({
                ...prev,
                status: event.target.value
              }))
            }
            onBlur={() => setTouched(true)}
          />
          </div>

          <div>
            <div className='mb-1 flex items-center justify-between gap-2'>
              <label className='block text-[12px] font-semibold capitalize text-gray-700'>
                {t('Payers')}
              </label>
              {dateRangeInput.payer_ids.length > 0 && (
                <button
                  type='button'
                  className='text-[10px] font-semibold text-[#0070C2] hover:underline'
                  onClick={() =>
                    setDateRangeInput(prev => ({
                      ...prev,
                      payer_ids: []
                    }))
                  }
                >
                  {t('All Payers')}
                </button>
              )}
            </div>
            <CustomFilterInput
              label=''
              name='payer_to_add'
              isSelect
              value={payerToAdd}
              placeholder={t('Select Payer')}
              onChange={event => {
                const payerId = event.target.value
                setPayerToAdd('')
                if (!payerId) return
                setDateRangeInput(prev => ({
                  ...prev,
                  payer_ids: prev.payer_ids.includes(payerId)
                    ? prev.payer_ids
                    : [...prev.payer_ids, payerId]
                }))
              }}
              renderOptions={() =>
                payerOptions.map(category => (
                  <optgroup
                    key={category.id || category.name_en}
                    label={
                      category?.name_en
                        ? `${category.name_en} (${category.license_id || ''})`
                        : t('Payers')
                    }
                  >
                    {(category?.payers || []).map(payer => (
                      <option
                        key={payer.id}
                        value={payer.id}
                        disabled={dateRangeInput.payer_ids.includes(payer.id)}
                      >
                        {payer.name_en || payer.name || payer.id}
                        {payer.license_id ? ` (${payer.license_id})` : ''}
                      </option>
                    ))}
                  </optgroup>
                ))
              }
            />
            {selectedPayerOptions.length > 0 && (
              <div className='mt-2 flex flex-wrap gap-1.5'>
                {selectedPayerOptions.map(payer => (
                  <span
                    key={payer.id}
                    className='inline-flex max-w-full items-center gap-1 rounded-full bg-[#0070C2]/10 px-2 py-1 text-[11px] font-semibold text-[#0070C2]'
                  >
                    <span className='truncate'>
                      {payer.name}
                      {payer.licenseId ? ` (${payer.licenseId})` : ''}
                    </span>
                    <button
                      type='button'
                      className='text-[12px] leading-none text-[#0070C2] hover:text-red-600'
                      onClick={() =>
                        setDateRangeInput(prev => ({
                          ...prev,
                          payer_ids: prev.payer_ids.filter(
                            payerId => payerId !== payer.id
                          )
                        }))
                      }
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className='mt-1 text-[10px] text-gray-500'>
              {dateRangeInput.payer_ids.length > 0
                ? `${t('Selected Payers')}: ${dateRangeInput.payer_ids.length}`
                : t('No payer selected means all payers')}
            </p>
          </div>
          {dateRangeValidationError && (
            <p className='text-[10px] font-medium text-red-500'>
              {dateRangeValidationError}
            </p>
          )}
        </div>
      )}

      {(!isReadyAction || readyMode === 'bundle') &&
        requestBundleIds.length > 0 && (
          <p className='text-xs text-gray-600'>
            {t('Total IDs')}: {requestBundleIds.length}
          </p>
        )}

      {errors.length > 0 && (
        <div className='rounded-md border border-red-200 bg-red-50 p-2'>
          {errors.map((error, index) => (
            <p className='text-xs text-red-700' key={`${error.code}-${index}`}>
              {error.code ? `${error.code}: ` : ''}
              {error.display}
            </p>
          ))}
        </div>
      )}

      <div className='flex justify-end gap-2 border-t border-gray-100 pt-3'>
        {!isInline && (
          <AppButton
            label={t('Cancel')}
            variant='outlined'
            onClick={handleClose}
            disabled={loading}
            className='!text-gray-600 !border-gray-300 hover:!bg-gray-100'
          />
        )}
        <AppButton
          label={t('Submit')}
          onClick={handleSubmit}
          isLoading={loading}
          loadingText={t('Submitting')}
        />
      </div>
    </div>
  )

  if (isInline) {
    return (
      <div className='w-full bg-white border border-gray-200 rounded-lg p-4 shadow-xs'>
        <div className='border-b border-gray-200 pb-2.5 mb-3 flex items-center justify-between flex-wrap gap-2'>
          <h3 className='text-sm font-bold text-gray-800 uppercase tracking-tight'>
            {t(config.title)}
          </h3>
          {renderModeToggle()}
        </div>
        {formBody}
      </div>
    )
  }

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title={t(config.title)}
      maxWidth='sm'
      contentSx={{ overflow: 'visible' }}
    >
      {formBody}
    </AppModal>
  )
}

export default BulkCancelBundleModal
